import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Auth, authState, signOut, User } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { PccAuthService } from '@ss/shared/auth';
import { SimpliscreenDialogService } from '@ss/shared/dialog';
import { firebaseConfig } from '@ss/typings/config';
import { differenceInMinutes } from 'date-fns';
import { debounceTime, firstValueFrom, Subject } from 'rxjs';
import { InactiveWarningDialog } from '../dialogs/inactive-warning/inactive-warning.dialog';
import { NtaFacilityStore } from './facility.store';

const EXPIRE_IN_MINUTES = 60;
const MILLIS_IN_MINUTE = 60000;
const EXPIRATION_WARNING_MINUTES = 5;
const EXPIRE_WARNING = (EXPIRE_IN_MINUTES - EXPIRATION_WARNING_MINUTES) * MILLIS_IN_MINUTE;
const LAST_ACTIVE_KEY = 'ntala';

@Injectable({ providedIn: 'root' })
export class InactivityService {
	private auth = inject(Auth);

	private router = inject(Router);

	private dialog = inject(SimpliscreenDialogService);

	private http = inject(HttpClient);

	private facilityStore = inject(NtaFacilityStore);

	private pccAuthService = inject(PccAuthService);

	lastActive: Date;

	user: User;

	active: Subject<void> = new Subject();

	firstLoad = true;

	constructor() {
		authState(this.auth).subscribe((user) => {
			this.user = user;

			if (this.user && this.firstLoad) {
				this.lastActive = new Date(+window.localStorage.getItem(LAST_ACTIVE_KEY));
				this.handleInactiveCheck();
			}
			this.firstLoad = false;
		});

		this.active.asObservable().pipe(debounceTime(EXPIRE_WARNING)).subscribe(() => {
			if (!this.user || this.isInactivityExempt()) return;
			this.dialog.open(InactiveWarningDialog, { expectResponse: true }).subscribe((response) => response ? this.updateLastActive() : this.signOut());
		});
	}

	updateLastActive() {
		this.lastActive = new Date();
		window.localStorage.setItem(LAST_ACTIVE_KEY, this.lastActive.getTime().toString());
		this.active.next();
	}

	async handleInactiveCheck() {

		if (!this.user) return;

		if (this.isInactivityExempt()) {
			this.updateLastActive();
			return;
		}

		const diffInMinutes = differenceInMinutes(new Date(), this.lastActive);
		if (diffInMinutes > EXPIRE_IN_MINUTES) await this.signOut();

		this.updateLastActive();
	}

	async signOut() {
		await this.revokeRefreshTokens();
		await signOut(this.auth);
		this.router.navigate(['account/signin']);
		try {
			await this.pccAuthService.logout();
		} finally {
			window.localStorage.removeItem(LAST_ACTIVE_KEY);
			location.reload();
		}
	}

	private async revokeRefreshTokens() {
		if (!this.user) return;

		try {
			await firstValueFrom(this.http.post(`${firebaseConfig.functionsUrl}/ntaApi/auth/revoke-refresh-tokens`, {}));
		} catch {
			// If token revocation fails, continue with local sign-out to avoid trapping the user in session.
		}
	}

	private isInactivityExempt(): boolean {
		return this.facilityStore.isSimpliroundsBaseAccount();
	}
}
